import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { paymentService, RazorpayResponse } from '../../services/paymentService.ts';
import { PaymentVerificationResponse } from '../../services/authApi.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import './PaymentSuccess.css';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserProfile } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verificationData, setVerificationData] = useState<PaymentVerificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get payment response from location state
        const paymentResponse = location.state?.paymentResponse as RazorpayResponse;

        if (!paymentResponse) {
          setError('Payment information not found');
          setVerifying(false);
          return;
        }

        // Verify payment on backend
        const response = await paymentService.verifyPayment(paymentResponse);

        if (response.data.success) {
          setVerificationData(response.data);
          // Refresh user data to update subscription status
          await refreshUserProfile();
          // Auto-redirect to screener after 3 seconds
          setTimeout(() => {
            navigate('/screener/momentum-ai', { replace: true });
          }, 3000);
        } else {
          setError('Payment verification failed');
        }
      } catch (err: any) {
        console.error('Payment verification error:', err);
        setError(err.response?.data?.message || 'Failed to verify payment');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [location, refreshUserProfile]);

  const handleContinue = () => {
    navigate('/screener/momentum-ai', { replace: true });
  };

  if (verifying) {
    return (
      <div className="payment-result-container">
        <div className="payment-result-card">
          <div className="spinner"></div>
          <h2>Verifying Payment...</h2>
          <p>Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-container">
        <div className="payment-result-card error">
          <div className="error-icon">✕</div>
          <h2>Verification Failed</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/pricing')} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-container">
      <div className="payment-result-card success">
        <div className="success-icon">✓</div>
        <h2>Payment Successful!</h2>
        <p className="success-message">
          Your subscription has been activated successfully.
        </p>
        <p className="redirect-message" style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
          Redirecting to screener in 3 seconds...
        </p>

        {verificationData && (
          <div className="subscription-details">
            <div className="detail-row">
              <span className="label">Plan:</span>
              <span className="value">{verificationData.planName}</span>
            </div>
            <div className="detail-row">
              <span className="label">Transaction ID:</span>
              <span className="value">{verificationData.transactionId}</span>
            </div>
            <div className="detail-row">
              <span className="label">Valid Until:</span>
              <span className="value">
                {new Date(verificationData.expiryDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Days Remaining:</span>
              <span className="value">{verificationData.daysRemaining} days</span>
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button onClick={handleContinue} className="btn-primary">
            Start Using Premium Features
          </button>
          <button onClick={() => navigate('/payments/history')} className="btn-secondary">
            View Payment History
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
